/**
 * Utilitário de Tempo Comercial
 * Horário comercial: Seg-Sex, 08:00 às 18:00 (Brasília)
 */

const HORA_INICIO = 8;  // 08:00
const HORA_FIM = 18;    // 18:00
const HORAS_DIA_UTIL = HORA_FIM - HORA_INICIO; // 10h úteis/dia

/**
 * Calcula o número de horas úteis entre duas datas
 * Considera apenas Seg-Sex, 08:00-18:00
 */
export function calcularHorasUteis(inicio: Date, fim: Date): number {
  if (fim <= inicio) return 0;

  let horasUteis = 0;
  let cursor = new Date(inicio);

  // Avança cursor para o próximo momento comercial se estiver fora
  cursor = proximoMomentoComercial(cursor);

  while (cursor < fim) {
    const diaDaSemana = cursor.getDay(); // 0=Dom, 6=Sab
    const ehDiaUtil = diaDaSemana >= 1 && diaDaSemana <= 5;

    if (ehDiaUtil) {
      const hora = cursor.getHours() + cursor.getMinutes() / 60;
      if (hora >= HORA_INICIO && hora < HORA_FIM) {
        // Calcula até o fim do expediente de hoje ou até `fim`, o que vier antes
        const fimExpedienteHoje = new Date(cursor);
        fimExpedienteHoje.setHours(HORA_FIM, 0, 0, 0);

        const ate = fim < fimExpedienteHoje ? fim : fimExpedienteHoje;
        const diffHoras = (ate.getTime() - cursor.getTime()) / (1000 * 60 * 60);
        horasUteis += diffHoras;

        cursor = fimExpedienteHoje;
      }
    }

    // Pula para o próximo dia útil 08:00
    if (cursor >= fim) break;
    cursor = proximoMomentoComercial(new Date(cursor.getTime() + 1000)); // +1s para avançar
  }

  return horasUteis;
}

/**
 * Retorna o próximo momento dentro do horário comercial (Seg-Sex 08:00-18:00)
 * Se já estiver dentro, retorna o mesmo instante
 */
function proximoMomentoComercial(date: Date): Date {
  const d = new Date(date);
  
  // Garante que estamos num dia útil
  let diaDaSemana = d.getDay();
  
  // Se for sábado (6) ou domingo (0), avança para segunda
  if (diaDaSemana === 6) {
    d.setDate(d.getDate() + 2);
    d.setHours(HORA_INICIO, 0, 0, 0);
    return d;
  }
  if (diaDaSemana === 0) {
    d.setDate(d.getDate() + 1);
    d.setHours(HORA_INICIO, 0, 0, 0);
    return d;
  }

  const hora = d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600;

  // Antes do expediente: avança para 08:00 de hoje
  if (hora < HORA_INICIO) {
    d.setHours(HORA_INICIO, 0, 0, 0);
    return d;
  }

  // Após o expediente: avança para 08:00 do próximo dia útil
  if (hora >= HORA_FIM) {
    d.setDate(d.getDate() + 1);
    d.setHours(HORA_INICIO, 0, 0, 0);
    // Recursivo para pular fim de semana
    return proximoMomentoComercial(d);
  }

  return d;
}

/**
 * Formata horas úteis em string legível
 * Ex: 0.5 → "30m", 2.5 → "2h30m", 25 → "2d5h"
 */
export function formatarTempoUtil(horasUteis: number): string {
  if (horasUteis < 0) horasUteis = 0;

  const totalMinutos = Math.floor(horasUteis * 60);
  const minutos = totalMinutos % 60;
  const totalHoras = Math.floor(totalMinutos / 60);
  const dias = Math.floor(totalHoras / HORAS_DIA_UTIL);
  const horas = totalHoras % HORAS_DIA_UTIL;

  if (dias > 0 && horas > 0) return `${dias}d${horas}h`;
  if (dias > 0) return `${dias}d`;
  if (horas > 0 && minutos > 0) return `${horas}h${minutos}m`;
  if (horas > 0) return `${horas}h`;
  if (minutos > 0) return `${minutos}m`;
  return '< 1m';
}

export type StatusSLA = 'ok' | 'atencao' | 'atrasado' | 'sem_prazo';

export interface ResultadoTempoTriagem {
  horasUteis: number;
  label: string;
  status: StatusSLA;
  percentual: number; // 0-1 (>1 = atrasado)
  prazoHoras: number | null;
}

/**
 * Calcula o tempo na triagem com base no SLA
 * @param entradaColunaEm - ISO string de quando entrou na coluna
 * @param prazoHoras - número de horas úteis do SLA, ou 'agendamento'
 * @param dataAgendamento - data de instalação (para Triagem Solar Fixa)
 */
export function calcularTempoTriagem(
  entradaColunaEm: string | null | undefined,
  prazoHoras: number | 'agendamento',
  dataAgendamento?: string | null,
): ResultadoTempoTriagem {
  if (!entradaColunaEm) {
    return { horasUteis: 0, label: '-', status: 'sem_prazo', percentual: 0, prazoHoras: null };
  }

  const agora = new Date();

  // Caso especial: prazo baseado em data de agendamento (Triagem Solar Fixa)
  if (prazoHoras === 'agendamento') {
    const horasUteis = calcularHorasUteis(new Date(entradaColunaEm), agora);
    const label = formatarTempoUtil(horasUteis);

    if (!dataAgendamento) {
      return { horasUteis, label, status: 'sem_prazo', percentual: 0, prazoHoras: null };
    }

    const dataInstalacao = new Date(
      dataAgendamento.includes('T') ? dataAgendamento : dataAgendamento + 'T23:59:59'
    );
    const atrasado = agora > dataInstalacao;
    return {
      horasUteis,
      label,
      status: atrasado ? 'atrasado' : 'ok',
      percentual: atrasado ? 1.5 : 0.5,
      prazoHoras: null,
    };
  }

  // Prazo em horas úteis
  const horasUteis = calcularHorasUteis(new Date(entradaColunaEm), agora);
  const percentual = horasUteis / prazoHoras;
  const label = formatarTempoUtil(horasUteis);

  let status: StatusSLA;
  if (percentual >= 1) status = 'atrasado';
  else if (percentual >= 0.8) status = 'atencao';
  else status = 'ok';

  return { horasUteis, label, status, percentual, prazoHoras };
}
